'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  Users,
  BadgeCheck,
  Briefcase,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Settings,
  Building2,
  CreditCard,
  BarChart3,
  Tag,
  Shield,
  ShieldCheck,
  BookOpen,
  HelpCircle,
  Star,
  MessageSquare,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badgeKey?: 'artisanVerification' | 'disputeManagement';
  group: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard, group: 'core' },
  { label: 'User Management', href: '/user-management', icon: Users, group: 'core' },
  { label: 'Artisan Verification', href: '/artisan-verification-queue', icon: BadgeCheck, badgeKey: 'artisanVerification', group: 'core' },
  { label: 'Jobs & Orders', href: '/jobs-orders', icon: Briefcase, group: 'operations' },
  { label: 'Dispute Management', href: '/dispute-management', icon: AlertTriangle, badgeKey: 'disputeManagement', group: 'operations' },
  { label: 'Organizations', href: '/dashboard', icon: Building2, group: 'operations' },
  { label: 'Payments', href: '/dashboard', icon: CreditCard, group: 'finance' },
  { label: 'Analytics', href: '/dashboard', icon: BarChart3, group: 'finance' },
  { label: 'Categories', href: '/dashboard', icon: Tag, group: 'platform' },
  { label: 'Blog Posts', href: '/blog-posts', icon: BookOpen, group: 'content' },
  { label: 'FAQs', href: '/faqs', icon: HelpCircle, group: 'content' },
  { label: 'Testimonials', href: '/testimonials', icon: Star, group: 'content' },
  { label: 'Contact Inbox', href: '/contact-submissions', icon: MessageSquare, group: 'content' },
  { label: 'Role Management', href: '/role-management', icon: ShieldCheck, group: 'platform', adminOnly: true },
  { label: 'Settings', href: '/dashboard', icon: Settings, group: 'platform' },
];

const groups: { key: string; label: string }[] = [
  { key: 'core', label: 'Core' },
  { key: 'operations', label: 'Operations' },
  { key: 'finance', label: 'Finance' },
  { key: 'content', label: 'Content' },
  { key: 'platform', label: 'Platform' },
];

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  finance: 'Finance',
  operations: 'Operations',
  support_admin: 'Support Admin',
  finance_admin: 'Finance Admin',
};

interface BadgeCounts {
  artisanVerification: number;
  disputeManagement: number;
}

export default function Sidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const { userProfile, adminRole, signOut, isSuperAdmin } = useAuth();
  const [badgeCounts, setBadgeCounts] = useState<BadgeCounts>({
    artisanVerification: 0,
    disputeManagement: 0,
  });

  useEffect(() => {
    async function fetchBadgeCounts() {
      const supabase = createClient();
      const [verificationResult, disputeResult] = await Promise.all([
        supabase
          .from('artisan_verifications')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'under_review'),
        supabase
          .from('disputes')
          .select('*', { count: 'exact', head: true })
          .in('status', ['open', 'mediation', 'escalated']),
      ]);

      setBadgeCounts({
        artisanVerification: verificationResult.count ?? 0,
        disputeManagement: disputeResult.count ?? 0,
      });
    }

    fetchBadgeCounts();
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const displayName = userProfile?.full_name || 'Admin';
  const displayRole = adminRole ? (ROLE_LABELS[adminRole] ?? adminRole.replace(/_/g, ' ')) : 'Admin';

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {}
  };

  return (
    <aside
      className="sidebar-transition flex flex-col h-screen bg-primary sticky top-0 shrink-0 overflow-hidden"
      style={{ width: collapsed ? '64px' : '240px' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10 shrink-0">
        <div className="shrink-0">
          <AppLogo size={32} />
        </div>
        {!collapsed && (
          <span className="font-bold text-white text-base tracking-tight truncate">
            KraftSouq
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3">
        {groups.map((group) => {
          const items = navItems.filter((n) => {
            if (n.group !== group.key) return false;
            if (n.adminOnly && !isSuperAdmin()) return false;
            return true;
          });
          if (items.length === 0) return null;
          return (
            <div key={`group-${group.key}`} className="mb-1">
              {!collapsed && (
                <p className="px-4 pt-3 pb-1 text-xs font-600 uppercase tracking-widest text-white/30">
                  {group.label}
                </p>
              )}
              {collapsed && <div className="h-3" />}
              {items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                const badgeCount = item.badgeKey ? badgeCounts[item.badgeKey] : 0;
                return (
                  <Link
                    key={`nav-${item.label}`}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`relative flex items-center gap-3 mx-2 my-0.5 px-3 py-2.5 rounded-lg text-sm font-500 transition-all duration-150 ${
                      active
                        ? 'bg-accent/20 text-accent border-r-2 border-accent' :'text-white/60 hover:bg-white/5 hover:text-white/90'
                    }`}
                  >
                    <Icon size={18} className="shrink-0" />
                    {!collapsed && (
                      <span className="truncate flex-1">{item.label}</span>
                    )}
                    {!collapsed && badgeCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-700 rounded-full px-1.5 py-0.5 min-w-[20px] text-center leading-none">
                        {badgeCount}
                      </span>
                    )}
                    {collapsed && badgeCount > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="shrink-0 border-t border-white/10 p-3">
        <div
          className={`flex items-center gap-3 px-2 py-2 rounded-lg mb-2 ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center shrink-0">
            <Shield size={14} className="text-white" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-600 truncate">{displayName}</p>
              <p className="text-white/40 text-xs truncate">{displayRole}</p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={handleSignOut}
              className="text-white/40 hover:text-white transition-colors"
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          )}
        </div>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 py-2 text-white/40 hover:text-white/80 text-xs transition-colors rounded-lg hover:bg-white/5"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}